import { channel_auth, channel_on_auth, channel_on_auth_error, channel_ping, channel_pong } from "../channel_names";
import { decode, encode } from "../encode_decode_message";
import { ConnectionState, RTMT, RTMT_WS_PING_INTERVAL, RTMT_WS_PING_INTERVAL_BUFFER_TIME, RtmtEventTypes, TokenProvider } from "./rtmt";
import { TinyEmitter } from "tiny-emitter";
import { AuthUser } from "../../models/AuthUser";

const MESSAGE_CHANNEL_PREFIX = "message_channel";

export class RTMTWS extends TinyEmitter implements RTMT {
  private webSocket?: WebSocket;
  private pingTimeout?: number = undefined;
  private _connectionState: ConnectionState = "none";
  private url: string = "";
  private getToken?: TokenProvider;
  private _user?: AuthUser;

  get connectionState(): ConnectionState {
    return this._connectionState;
  }

  get user(): AuthUser | undefined {
    return this._user;
  }

  protected setConnectionState(connectionState: ConnectionState) {
    this._connectionState = connectionState;
    this.emit("connectionchange", this._connectionState);
  }

  private createWebSocket(url: string) {
    const webSocket = new WebSocket(url);
    webSocket.onopen = () => this.onWebSocketOpen(webSocket);
    webSocket.onclose = () => this.onWebSocketClose(webSocket);
    webSocket.onmessage = (event: any) => this.onWebSocketMessage(webSocket, event);
    webSocket.onerror = (error: any) => this.onWebSocketError(webSocket, error);
  }

  private disposeWebSocket() {
    if (!this.webSocket) return;
    this.webSocket.onopen = () => { };
    this.webSocket.onclose = () => { };
    this.webSocket.onmessage = (event: any) => { };
    this.webSocket.onerror = (error: any) => { };
    this.webSocket = undefined;
  }

  // With getToken the connection authenticates with a token and is "connected" once the server accepts it.
  public connectWebSocket(url: string, getToken?: TokenProvider) {
    this.setConnectionState("connecting");
    this.url = url;
    this.getToken = getToken;
    this.createWebSocket(url);
  }

  // Sends a refreshed token for the current connection.
  public reauthenticate(token: string) {
    if (this._connectionState !== "connected" || !this.getToken) return;
    this.sendMessage(channel_auth, { token });
  }

  private reconnectWebSocket() {
    this.setConnectionState("reconnecting");
    this.disposeWebSocket();
    setTimeout(() => this.createWebSocket(this.url), 1000);
  }

  protected onWebSocketOpen(webSocket: WebSocket) {
    this.webSocket = webSocket;
    console.info("(RTMT) WebSocket has opened");
    this.heartbeat();
    if (!this.getToken) {
      this.setConnectionState("connected");
      this.emit("open");
      return;
    }
    this.getToken().then((token) => {
      if (this.webSocket === webSocket) this.sendMessage(channel_auth, { token });
    }).catch((error) => {
      console.error("(RTMT) Could not get a token", error);
      this.emit("error", error);
      webSocket.close();
    });
  }

  private onAuthenticated(user: AuthUser) {
    const isNewConnection = this._connectionState !== "connected";
    this._user = user;
    this.emit("authenticated", user);
    if (isNewConnection) {
      this.setConnectionState("connected");
      this.emit("open");
    }
  }

  protected onWebSocketMessage(webSocket: WebSocket, event: any) {
    const { channel, message } = decode(event.data);
    this.onMessage(channel, message);
  }

  protected onWebSocketError(webSocket: WebSocket, error: any) {
    this.setConnectionState("error");
    console.error(error);
    this.emit("error", error);
  }

  protected onWebSocketClose(webSocket: WebSocket) {
    this.setConnectionState("closed");
    console.info("(RTMT) WebSocket has closed");
    //this.WebSocket = undefined
    clearTimeout(this.pingTimeout);
    this.emit("close");
    this.reconnectWebSocket();
  }

  private heartbeat() {
    clearTimeout(this.pingTimeout);
    // @ts-ignore
    this.pingTimeout = setTimeout(() => {
      if (!this.webSocket) return;
      console.log("(RTMT) WebSocket self closed");
      this.webSocket.close();
      this.onWebSocketClose(this.webSocket);
    }, RTMT_WS_PING_INTERVAL + RTMT_WS_PING_INTERVAL_BUFFER_TIME);
  }

  public sendMessage(channel: string, message: Object) {
    if (this.webSocket === undefined) {
      console.error("(RTMT) WebSocket is undefined");
      return;
    }
    const data = encode({channel, message});
    this.webSocket.send(data);
  }

  private onMessage(channel: string, message: Object) {
    if (channel === channel_ping) {
      this.heartbeat();
      this.sendMessage(channel_pong, {});
      return;
    }
    if (channel === channel_on_auth) {
      this.onAuthenticated((message as { user: AuthUser }).user);
      return;
    }
    if (channel === channel_on_auth_error) {
      // The server closes the socket next; reconnecting fetches a fresh token.
      console.error("(RTMT) Authentication failed", message);
      return;
    }
    // TODO: Maybe we should warn if there is not any listener for channel
    this.emit(MESSAGE_CHANNEL_PREFIX + channel, message);
  }

  public on(event: RtmtEventTypes, callback: (...value: any[]) => void): this {
    return super.on(event, callback);
  }

  public off(event: RtmtEventTypes, callback: (...value: any[]) => void): this {
    return super.off(event, callback);
  }

  public addMessageListener(channel: string, callback: (message: any) => void) {
    super.on(MESSAGE_CHANNEL_PREFIX + channel, callback);
  }

  public removeMessageListener(channel: string, callback: (message: any) => void) {
    super.off(MESSAGE_CHANNEL_PREFIX + channel, callback);
  }

  public dispose() {
    this.disposeWebSocket();
    // TODO
    //this.removeAllListeners();
  }
}
